import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRightIcon, XIcon } from 'lucide-react';
import type { GallerySite } from '../../data/gallery';
type GalleryPreviewModalProps = {
  site: GallerySite | null;
  onClose: () => void;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function GalleryPreviewModal({
  site,
  onClose
}: GalleryPreviewModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!site) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [site, onClose]);
  return <AnimatePresence data-id="element-423" data-mp="2nzaxfezebkge">
      {site && <motion.div key="backdrop" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-3 sm:p-6" onClick={onClose} data-id="element-424" data-mp="2nyhvqsfanl54">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="gallery-preview-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.25,
        ease
      }} onClick={e => e.stopPropagation()} className="flex max-h-full w-full max-w-[min(100%,72rem)] flex-col overflow-hidden rounded-xl bg-white shadow-2xl" data-id="element-425" data-mp="24asdpcx5sz93">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 px-5 py-4 sm:px-6" data-id="element-426" data-mp="1qilv7k6jag6h">
              <div className="min-w-0" data-id="element-427" data-mp="1ih4rwz9t1j17">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-brand-red" data-id="element-428" data-mp="1eh2niicoqbi8">
                  {site.industry}
                  {site.type === 'landing page' && <span className="text-brand-gray" data-id="element-429" data-mp="1s7zi2eq692rx"> · Landing page</span>}
                </p>
                <h2 id="gallery-preview-title" className="mt-0.5 truncate text-xl font-bold text-brand-black" data-id="element-430" data-mp="1n9xxtqh0exev">
                  {site.name}
                </h2>
              </div>
              <div className="flex items-center gap-3" data-id="element-431" data-mp="vj9z0l2t644d">
                {site.url && <a href={site.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-brand-red px-4 py-2.5 text-sm font-bold text-white transition-colors duration-150 ease-out hover:bg-brand-red-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2" data-id="element-432" data-mp="19i9kxkyxs4wu">
                    Visit live site
                    <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-433" data-mp="6qgkgmyuwq2d" />
                  </a>}
                <button ref={closeRef} type="button" onClick={onClose} aria-label="Close preview" className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 text-brand-black transition-colors duration-150 ease-out hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red" data-id="element-434" data-mp="3owbn3o1zd9vp">
                  <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-435" data-mp="bmnp8cfinviq" />
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto bg-gray-100" data-id="element-436" data-mp="14cm9luirn4h2">
              <img src={site.image} alt={`Full homepage of ${site.name}`} decoding="async" className="block h-auto w-full" data-id="element-437" data-mp="193vxoqoo1zqu" />
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}